'use client'

import * as React from 'react'
import { useInView, useReducedMotion, animate } from 'framer-motion'
import { EASE } from '@/lib/motion'

// Animated PageSpeed Insights gauge: the arc draws and the number counts
// up from 0 the first time it scrolls into view, like the real Lighthouse
// result rendering. Coloured by Google's score bands. Reduced motion shows
// the final value instantly.

// Google's score bands: 0-49 red, 50-89 amber, 90-100 green.
export function band(value: number) {
  if (value >= 90) return { stroke: '#0cce6b', text: '#0a7c41', fill: '#0cce6b' }
  if (value >= 50) return { stroke: '#ffa400', text: '#9a6700', fill: '#ffa400' }
  return { stroke: '#ff4e42', text: '#c5221f', fill: '#ff4e42' }
}

export function PageSpeedGauge({ value }: { value: number }) {
  const ref = React.useRef<SVGSVGElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const [shown, setShown] = React.useState(0)

  React.useEffect(() => {
    if (reduce) {
      setShown(value)
      return
    }
    if (!inView) return
    const controls = animate(0, value, { duration: 1.1, ease: EASE, onUpdate: (v) => setShown(v) })
    return () => controls.stop()
  }, [inView, reduce, value])

  const r = 32
  const c = 2 * Math.PI * r
  const offset = c * (1 - shown / 100)
  const col = band(value)

  return (
    <svg ref={ref} viewBox="0 0 80 80" className="h-[78px] w-[78px]" role="img" aria-label={`Score ${value} out of 100`}>
      {/* translucent inner disc, as in the PSI gauge */}
      <circle cx="40" cy="40" r="35" fill={col.fill} fillOpacity="0.10" />
      <g transform="rotate(-90 40 40)">
        {/* faint full-ring track */}
        <circle cx="40" cy="40" r={r} fill="none" stroke={col.stroke} strokeOpacity="0.20" strokeWidth="5" />
        {/* score arc — draws as `shown` rises */}
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke={col.stroke}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </g>
      <text x="40" y="41" textAnchor="middle" dominantBaseline="middle" className="font-display text-[24px] font-semibold" fill={col.text}>
        {Math.round(shown)}
      </text>
    </svg>
  )
}
